import { useCallback } from 'react'

/**
 * Preview song form state for localStorage persistence (generic, not user-specific)
 */
export interface PreviewSongFormState {
  title: string
  tags: string
  lyrics: string
  theme: string
}

const STORAGE_KEY = 'jinglearn_preview_song_form'

/**
 * Custom hook for managing preview song form state in localStorage
 *
 * Features:
 * - Generic storage (not user-specific)
 * - Only saves values with length > 0
 * - Provides load, save, and clear functions
 *
 * @returns Object with loadFormState, saveFormState, and clearFormState functions
 */
export function usePreviewSongStorage() {
  /**
   * Load saved form state from localStorage
   */
  const loadFormState = useCallback((): PreviewSongFormState | null => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved) as PreviewSongFormState
        return parsed
      }
    } catch (error) {
      console.error('Failed to load preview song form state from localStorage:', error)
    }
    return null
  }, [])

  /**
   * Save form state to localStorage
   * Only saves values with length > 0
   */
  const saveFormState = useCallback((state: PreviewSongFormState) => {
    try {
      // Filter out empty strings
      const stateToSave: Partial<PreviewSongFormState> = {}

      if (state.title && state.title.length > 0) {
        stateToSave.title = state.title
      }
      if (state.tags && state.tags.length > 0) {
        stateToSave.tags = state.tags
      }
      if (state.lyrics && state.lyrics.length > 0) {
        stateToSave.lyrics = state.lyrics
      }
      if (state.theme && state.theme.length > 0) {
        stateToSave.theme = state.theme
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave))
    } catch (error) {
      console.error('Failed to save preview song form state to localStorage:', error)
    }
  }, [])

  /**
   * Clear all form state from localStorage
   */
  const clearFormState = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (error) {
      console.error('Failed to clear preview song form state from localStorage:', error)
    }
  }, [])

  return {
    loadFormState,
    saveFormState,
    clearFormState,
  }
}

